import { orderBy } from "lodash";

import { SmartphoneFamilyDescription } from "shared/product/ProductSection/product-section.helper";
import { initializeApollo } from "utils/with-apollo.utils";

import {
  ComparatorDataDocument,
  ComparatorDataQuery,
} from "./comparator-data.query.generated";
import { ComparatorRivalsDocument, ComparatorRivalsQuery } from "./comparator-rivals.query.generated";

type SmartphoneFamiliesDataAttributes = NonNullable<
  NonNullable<ComparatorDataQuery["smartphoneFamilies"]>["data"]
>[number]["attributes"];

export type ModelDetail = {
  slug: NonNullable<SmartphoneFamiliesDataAttributes>["slug"];
  modelName: NonNullable<SmartphoneFamiliesDataAttributes>["model"];
  description: SmartphoneFamilyDescription;
};

export type ComparatorWinner = {
  slug: string;
  highlights: {
    name: string;
    value: string;
  }[];
} | null;

export const getComparatorData = async () => {
  const apolloClient = initializeApollo();

  const { data: comparatorData } =
    await apolloClient.query<ComparatorDataQuery>({
      query: ComparatorDataDocument,
    });

  const { modelDetails, slugs } =
    comparatorData?.smartphoneFamilies?.data?.reduce(
      (acc, { attributes }) => {
        const modelDetail: ModelDetail = {
          slug: attributes?.slug ?? "",
          modelName: attributes?.model ?? "",
          description: attributes?.description,
        };

        acc.modelDetails.push(modelDetail);
        acc.slugs.push(modelDetail.slug);

        return acc;
      },
      { modelDetails: [] as ModelDetail[], slugs: [] as string[] },
    ) ?? { modelDetails: [] as ModelDetail[], slugs: [] as string[] };

  return {
    modelDetails,
    slugs: orderBy(slugs, [], ["desc"]),
  };
};

export const getComparatorWinner = async (rivalSlugA: string, rivalSlugB: string) => {
  const apolloClient = initializeApollo();

  const { data: rivalsData } = await apolloClient.query<ComparatorRivalsQuery>({
    query: ComparatorRivalsDocument,
  });

  const winnerData = rivalsData.comparatorRivals?.data.find((r) =>
    (r.attributes?.rivalA?.data?.attributes?.slug === rivalSlugA && r.attributes?.rivalB?.data?.attributes?.slug === rivalSlugB) ||
    (r.attributes?.rivalA?.data?.attributes?.slug === rivalSlugB && r.attributes?.rivalB?.data?.attributes?.slug === rivalSlugA),
  );

  const winner: ComparatorWinner = {
    slug: winnerData?.attributes?.winner?.data?.attributes?.slug || "",
    highlights: (winnerData?.attributes?.highlights ?? []).map((highlight) => ({
      name: highlight?.name || "",
      value: highlight?.text || "",
    }))
  };

  return {
    winner: winnerData ? winner : null,
  }
};